import { useState, useEffect, useCallback } from 'react'
import useSWR from 'swr'
import type { KeyedMutator } from 'swr'
import type { GenericListStateMachine } from 'types/api/common'

import type { AxiosResponseType, CommonError } from 'types/api/responses/common'
import type { SenderIDFetchResponse, SenderID } from 'types/api/responses/senderIds'
import type { AccountId } from 'types/auth'

import { getAccountSenderIds } from 'api/senderId'

type Params = {
	is_survey?: boolean
	page?: number
	per?: number
}
type Props = {
	accountId: AccountId
	params?: Params
}

export function useAccountSenderIds({ accountId, params }: Props): {
	data: AxiosResponseType<SenderIDFetchResponse> | undefined
	error: CommonError | undefined
	mutate: KeyedMutator<AxiosResponseType<SenderIDFetchResponse>>
} {
	const { data, error, mutate } = useSWR<AxiosResponseType<SenderIDFetchResponse>, CommonError>(
		[`/accounts/${accountId.value}/sender_ids`, accountId.value, params?.is_survey, params?.page, params?.per],
		() => getAccountSenderIds({ accountId: accountId.value, params }),
	)

	return {
		mutate,
		data,
		error,
	}
}

type SenderIDState = GenericListStateMachine<Array<SenderID>>

type PropsWithError = Props & {
	setErrorMessage: (message: string) => void
}

export function useAccountSenderIdsWithState({ accountId, setErrorMessage, params }: PropsWithError): {
	refreshSenderIds: () => void
	state: SenderIDState
} {
	const [state, setState] = useState<SenderIDState>({ status: 'idle' })
	const { data, error, mutate } = useAccountSenderIds({ accountId, params })

	useEffect(() => {
		if (data) {
			if (data.data?.sender_ids?.length === 0)
				return setState(() => ({
					status: 'empty',
				}))
			return setState({ status: 'resolved', data: data.data?.sender_ids ?? [] })
		}

		if (error) {
			setErrorMessage(error.errorMessage)
			if (error.isNetworkError) {
				return setState({ status: 'networkError' })
			}
			return setState({ status: 'rejected' })
		}
		if (!error && !data) { setState({ status: 'pending' }); return }
	}, [data, error, setErrorMessage])

	const refreshSenderIds = useCallback(() => {
		mutate()
	}, [mutate])

	return {
		state,
		refreshSenderIds,
	}
}
